import React, { useContext, useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import clsx from 'clsx'
import { useAtomValue, useSetAtom } from 'jotai/react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useDebounce } from '@node-space/hooks'
import PageHeader from '@node-space/storybook-components/dist/PageHeader'
import {
  Box,
  Button,
  Box as Container,
  Icon,
  Sheet,
  SidePanel,
  useSidePanel,
} from '@node-space/ui-components'
import { RequestErrorStatusCard } from 'components/Errors/RequestErrorStatusCard'
import TablePagination from 'components/PageableTablePagination'
import { UsEntityAgreementNames } from 'constants/agreements'
import { useProfileContext } from 'hooks/context/useProfileContext'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useCustomerQuery } from 'hooks/queries/Customers/useCustomerQuery'
import { useGetCustomersWalletsQuery } from 'hooks/queries/Customers/useGetCustomersWallets'
import { useEmbeddedCustomerAgreementsQuery } from 'hooks/queries/useEmbeddedCustomerAgreementsQuery'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePaginationPagingPersist } from 'hooks/usePaginationPagingPersist'
import { currentCustomerAtom, customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerInfoSkeletonLoader } from 'pages/Customers/CustomerView/CustomerInfoSkeletonLoader'
import { CustomerLayout } from 'pages/Customers/CustomerView/CustomerLayout'
import { CustomerWallets } from 'pages/Customers/CustomerView/CustomerWallets'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { EMBEDDED_CUSTOMER_AGREEMENT_STATUSES } from 'pages/EmbeddedCustomers/helpers/constants'
import { AddWallet } from 'pages/NestedCustomers/components/AddWallet'
import CustomerInfo from 'pages/NestedCustomers/components/CustomerInfo'
import CustomerWalletSidepanelDetails from 'pages/NestedCustomers/components/CustomerWallet/CustomerWalletSidepanelDetails'
import CustomerWalletSidePanelHeader from 'pages/NestedCustomers/components/CustomerWallet/CustomerWalletSidePanelHeader'
import CustomerWalletsSkeletonLoader from 'pages/NestedCustomers/components/CustomerWallet/CustomerWalletsSkeletonLoader'
import { useCustomerWalletDepositDetails } from 'pages/NestedCustomers/hooks/useCustomerWalletDepositDetails'
import { useCustomerWalletsTableRowData } from 'pages/NestedCustomers/hooks/useCustomerWalletsTableRowData'
import { TransactionExportModal } from 'pages/WalletDetails/components/RecentActivity/WalletTransactionsExport/TransactionExportModal'
import WalletModal, { WalletAction } from 'pages/Wallets/components/Modal/WalletModal'
import {
  fiatSendFormAtom,
  selectedFiatBeneficiaryAtom,
  selectedWalletAtom,
} from 'pages/Wallets/components/Pay/atoms/atoms'
import { WalletSheet } from 'pages/Wallets/components/Sheet/WalletSheet'
import { WalletActionContext } from 'pages/Wallets/context/WalletSheetProvider'
import { reactQueryKeys, reactQueryName } from 'reactQueryKeys/reactQueryKeys'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import {
  CustomerStatus,
  CustomerWallet,
  CustomerWalletDepositDetails,
  NestedCustomerDetails,
} from 'types/customers'
import { PaginationPaging } from 'types/pagination'
import { ActionIds, WalletTypes } from 'types/types'
import { WalletTransferType } from 'types/unifiedPayouts/unifiedPayouts'
import { PayActionBeneficiaryTypes } from 'types/wallets'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { highlightRowBasedOnId } from 'utils/table/table'

export const Customer = () => {
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  const { id: customerReference }: { id?: string } = useParams()
  const { canTrade, canEdit } = useMappedRoles()?.wallets || {}
  const { viewOnly: isViewOnlyRole, profile } = useProfileContext() || {}
  const [, setWalletAction] = useContext(WalletActionContext)

  const setBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setFiatFormAtom = useSetAtom(fiatSendFormAtom)
  const setFiatBeneficiaryAtom = useSetAtom(selectedFiatBeneficiaryAtom)
  const setSourceWalletAtom = useSetAtom(selectedWalletAtom)
  const customerModel = useAtomValue(customerModelAtom)
  const setCurrentCustomerAtom = useSetAtom(currentCustomerAtom)

  const [searchQuery, setSearchQuery] = useState<string>('')
  const debouncedSearchTerm = useDebounce(searchQuery, 500)

  const [walletModalAction, setWalletModalAction] = useState<WalletAction>(null)
  const [isPayWalletModalOpen, setIsPayWalletModalOpen] = useState(false)
  const [isAddWalletSheetOpen, setIsAddWalletSheetOpen] = useState<boolean>(false)
  const [isSheetOpen, setIsSheetOpen] = useState<boolean>(false)
  const [currentCustomerWallet, setCurrentCustomerWallet] = useState<CustomerWallet>()
  const [page, setPage] = useState<number>(0)
  const [isTransactionExportModalVisible, setIsTransactionExportModalVisible] = useState(false)

  const isEmbeddedCustomer = customerModel === CustomerModels.EMBEDDED

  const {
    data: customer,
    isFetching: isFetchingCustomerInfo,
    isError: isErrorFetchingCustomerInfo,
    refetch: refetchCustomerInfo,
  } = useCustomerQuery<NestedCustomerDetails>(customerReference)

  const {
    data: { content: customerWallets, pagination: customerWalletPagination } = {},
    isFetching: isFetchingCustomerWallets,
    isError: isErrorFetchingCustomerWallets,
    refetch: fetchCustomerWallets,
  } = useGetCustomersWalletsQuery({
    customerReference,
    page,
    currencyCodeSearch: debouncedSearchTerm,
  })

  const {
    data: agreementsData = [],
    isFetching: isFetchingAgreements,
    isError: isErrorFetchingAgreements,
    refetch: refetchAgreements,
  } = useEmbeddedCustomerAgreementsQuery(isEmbeddedCustomer && customerReference)

  const isFetchingWalletsData = useMemo(
    () => isFetchingCustomerWallets || isFetchingCustomerInfo || isFetchingAgreements,
    [isFetchingCustomerWallets, isFetchingCustomerInfo, isFetchingAgreements]
  )

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (customerWallets?.length) {
      const walletLsids = customerWallets?.map(wallet => wallet?.id)

      return getWalletActionCapabilityRequestFilters(walletLsids)
    }
  }, [customerWallets])

  const {
    data: bulkCapabilities,
    isFetching: isFetchingCapabilities,
    isError: isErrorFetchingCapabilities,
  } = useBulkCapabiltiesQuery(fiatWalletCapabilitiesRequest, !!fiatWalletCapabilitiesRequest)

  const { paging, showPagination } = usePaginationPagingPersist(customerWalletPagination)

  useEffect(() => {
    setCurrentCustomerWallet(null)
  }, [])

  const { customerDetails, isFetchingCountries } = useCustomerWalletDepositDetails(customer)

  useEffect(() => {
    setCurrentCustomerAtom(customer)
    return () => {
      setCurrentCustomerAtom(null)
    }
  }, [customer])

  const handleOnAddCustomerWallet = () => {
    setIsAddWalletSheetOpen(true)
  }

  const handleAddWalletSheetClose = () => {
    setIsAddWalletSheetOpen(false)
  }
  const {
    isSidePanelOpen: isCustomerWalletSidePanelOpen,
    sidePanelData: customerWalletData,
    paging: pagingCustomerWalletPaging,
    setIsSidePanelOpen: setIsCustomerWalletSidePanelOpen,
    updateSidePanel: updateCustomerWalletSidePanel,
  } = useSidePanel(customerWallets)

  const handleOnDetailsClick = (index: number) => {
    updateCustomerWalletSidePanel(index)
    setIsCustomerWalletSidePanelOpen(true)
  }

  const handleOpenFiatDepositSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet) {
      const customerWalletDepositDetails: CustomerWalletDepositDetails = {
        wallet: customerWallet,
        customer: customer,
      }
      setWalletAction({
        actionId: ActionIds?.FIAT_DEPOSIT,
        header: t('deposit'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWalletDepositDetails,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleOpenMoveSheet = (wallet: CustomerWallet) => {
    if (wallet) {
      setWalletAction({
        actionId: ActionIds.MOVE,
        header: t('customers.moveFunds'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          sourceWalletType: WalletTransferType?.CUSTOMER,
          sourceCustomerWallet: wallet,
          isEmbeddedCustomerModel: isEmbeddedCustomer,
        },
      })
    }
    setIsSheetOpen(true)
  }

  const handleOnPayClick = (wallet: CustomerWallet) => {
    const walletAction: WalletAction = {
      key: 'pay',
      data: {
        id: wallet?.id,
      },
      metadata: {
        pay: {
          beneficiaryPaymentType: PayActionBeneficiaryTypes.UNDERLYING_CUSTOMER,
          customerReference: wallet?.customerReference,
          customerWalletId: wallet?.id,
        },
      },
    }

    setWalletModalAction(walletAction)
    setIsPayWalletModalOpen(true)
  }

  const handleExportTransactionClick = (customerWallet: CustomerWallet) => {
    setCurrentCustomerWallet(customerWallet)
    setIsTransactionExportModalVisible(true)
  }

  const handleOnExportModalClose = () => {
    setIsTransactionExportModalVisible(false)
    setCurrentCustomerWallet(null)
  }

  const cleanupUnifiedPayoutsFlow = () => {
    setBeneficiaryAtom(null)
    setFiatFormAtom(null)
    setFiatBeneficiaryAtom(null)
    setSourceWalletAtom(null)
    setWalletAction(null)
    setIsSheetOpen(false)
  }

  const handleOpenSendFiatSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet) {
      setWalletAction({
        actionId: ActionIds?.FIAT_SEND,
        header: t('wallets.fiatSend.beneficiaryStepHeader'),
        onClose: cleanupUnifiedPayoutsFlow,
        sourceWalletReference: customerWallet?.id,
        meta: {
          customerWallet: customerWallet,
        },
      })

      setIsSheetOpen(true)
    }
  }

  const handleOpenSendCryptoSheet = (customerWallet: CustomerWallet) => {
    if (customerWallet) {
      setWalletAction({
        actionId: ActionIds?.SEND,
        header: t('wallets.send.contactStepHeader'),
        onClose: () => {
          setIsSheetOpen(false)
        },
        meta: {
          customerWallet: customerWallet,
        },
      })
      setIsSheetOpen(true)
    }
  }

  const isActiveCustomer = customer?.status === CustomerStatus.VERIFIED
  const walletTableRows = useCustomerWalletsTableRowData({
    bulkCapabilities,
    isFetchingCapabilities,
    customerWallets,
    isActiveCustomer,
    handleExportTransactionClick,
    handleOnDetailsClick,
    handleOpenMoveSheet,
    handleOnPayClick,
    handleOpenFiatDepositSheet,
    handleOpenSendFiatSheet,
    handleOpenSendCryptoSheet,
  })

  const refreshWallets = async () => {
    await queryClient.invalidateQueries({ queryKey: reactQueryKeys.walletsV2(WalletTypes.FIAT) })
    await queryClient.invalidateQueries({
      queryKey: [reactQueryName.CUSTOMER_WALLETS],
    })
    await queryClient.invalidateQueries({
      queryKey: reactQueryKeys.totalBalance(profile?.currencyCode),
    })
  }

  const onReloadWalletsData = () => {
    isErrorFetchingCustomerWallets && fetchCustomerWallets()
    isErrorFetchingAgreements && refetchAgreements()
  }

  const hasSignedUsAgreement = agreementsData?.some(
    ({ status, name }) =>
      status === EMBEDDED_CUSTOMER_AGREEMENT_STATUSES.SIGNED &&
      Object.values(UsEntityAgreementNames)?.includes(name)
  )
  const isNonUsEmbeddedCustomer = isEmbeddedCustomer && !hasSignedUsAgreement
  const hasWalletAccessUserRole = canEdit || canTrade
  const hasWalletAccessCustomer = isActiveCustomer && !isNonUsEmbeddedCustomer
  const isAddWalletEnabled =
    !isFetchingWalletsData && hasWalletAccessUserRole && hasWalletAccessCustomer

  const AddWalletButton = isAddWalletEnabled && (
    <Button
      iconElement={<Icon name="Plus" />}
      disabled={isViewOnlyRole}
      onClick={handleOnAddCustomerWallet}
    >
      {t('customers.addWallet')}
    </Button>
  )

  const CustomerWalletsView = useMemo(() => {
    if (isFetchingWalletsData || isFetchingCapabilities) {
      return <CustomerWalletsSkeletonLoader />
    }
    if (
      isErrorFetchingCustomerWallets ||
      isErrorFetchingAgreements ||
      isErrorFetchingCapabilities
    ) {
      return <RequestErrorStatusCard onReload={onReloadWalletsData} />
    }
  }, [
    isFetchingWalletsData,
    isErrorFetchingCustomerWallets,
    isErrorFetchingAgreements,
    isFetchingCapabilities,
    isErrorFetchingCapabilities,
  ])

  const CustomerInfoView = useMemo(() => {
    if (isFetchingCustomerInfo || isFetchingCountries) {
      return <CustomerInfoSkeletonLoader />
    }
    if (customerDetails) {
      return <CustomerInfo customerDetails={customerDetails} />
    }
  }, [isFetchingCustomerInfo, isFetchingCountries, customerDetails])

  return (
    <>
      {AddWalletButton && <PageHeader renderRightSection={AddWalletButton} />}
      {isErrorFetchingCustomerInfo ? (
        <RequestErrorStatusCard onReload={refetchCustomerInfo} borderless />
      ) : (
        <CustomerLayout
          CustomerInfo={CustomerInfoView}
          CustomerWallets={
            <>
              {CustomerWalletsView ?? (
                <CustomerWallets
                  isCustomerVerified={isActiveCustomer}
                  customerName={customerDetails?.name}
                  isSearching={!!debouncedSearchTerm}
                  searchQuery={searchQuery}
                  walletTableRows={walletTableRows}
                  AddWalletButton={AddWalletButton}
                  onChangeSearchQuery={query => setSearchQuery(query)}
                />
              )}
              {!isErrorFetchingCustomerWallets && !!paging && !showPagination && (
                <Box
                  className={clsx({
                    'opacity-25': isFetchingCustomerWallets || showPagination,
                  })}
                >
                  <TablePagination
                    disableLeftDropdown
                    total={paging?.total}
                    pagingMax={paging?.max}
                    onPagination={({ page }: PaginationPaging) => setPage(page)}
                    resetPageNumber={debouncedSearchTerm}
                  />
                </Box>
              )}
            </>
          }
        />
      )}

      <SidePanel
        id={'side-panel-customer-wallet'}
        isOpen={isCustomerWalletSidePanelOpen}
        onClose={() => setIsCustomerWalletSidePanelOpen(false)}
        paging={pagingCustomerWalletPaging}
        bodyScrollable={false}
        activeRowId={customerWalletData?.id}
        onActiveRowChange={highlightRowBasedOnId}
        header={<CustomerWalletSidePanelHeader customerWalletData={customerWalletData} />}
        body={
          <Container flex flexCol gapY={12} width="full">
            <CustomerWalletSidepanelDetails customerWalletData={customerWalletData} />
          </Container>
        }
      />

      <Sheet
        isOpen={isAddWalletSheetOpen}
        header={t('customers.newWallet')}
        onClose={handleAddWalletSheetClose}
      >
        <AddWallet walletType={WalletTypes?.CUSTOMER} handleClose={handleAddWalletSheetClose} />
      </Sheet>

      <WalletSheet isSheetOpen={isSheetOpen} />

      {isPayWalletModalOpen && (
        <WalletModal
          action={walletModalAction}
          setWalletModalAction={setWalletModalAction}
          refresh={refreshWallets}
          setShowWalletModal={setIsPayWalletModalOpen}
        />
      )}

      {isTransactionExportModalVisible && (
        <TransactionExportModal
          key={currentCustomerWallet?.id}
          handleClose={handleOnExportModalClose}
          showCustomDateExportModal={isTransactionExportModalVisible}
          downloadAll={false}
          customerWallet={currentCustomerWallet}
        />
      )}
    </>
  )
}
